import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollText, Loader2, RefreshCw } from "lucide-react";

const ACTION_COLOR = {
  read: "text-sky-400 border-sky-500/30 bg-sky-500/5",
  write: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
  delete: "text-red-400 border-red-500/30 bg-red-500/5",
  approval: "text-amber-400 border-amber-500/30 bg-amber-500/5",
  consent_change: "text-purple-400 border-purple-500/30 bg-purple-500/5",
  export: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
};
const RESULT_COLOR = { blocked: "text-red-400", pending: "text-amber-400", success: "text-muted-foreground" };

export default function Audit() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ action: "", resource_type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      const { data } = await api.get("/compliance/audit", { params });
      setEntries(data.entries);
      setTotal(data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="audit-page">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-2">Compliance</p>
          <h1 className="font-display text-2xl flex items-center gap-2"><ScrollText className="w-5 h-5" /> Audit log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} total events · 365-day retention · showing latest {entries.length}
          </p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          data-testid="audit-filter-action"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All actions</option>
          {Object.keys(ACTION_COLOR).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Input value={filters.resource_type} onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
          placeholder="Resource type (e.g. entity_records)" className="h-9 max-w-xs" data-testid="audit-filter-resource" />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="audit-empty">No matching audit events.</p>
      ) : (
        <div className="card-flat divide-y divide-border/60" data-testid="audit-list">
          {entries.map((e) => (
            <div key={e.id} className="p-3.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`audit-row-${e.id}`}>
              <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${ACTION_COLOR[e.action] || "border-border"}`}>
                {e.action}
              </span>
              <span className="text-xs font-mono text-muted-foreground">{e.resource_type}</span>
              {e.resource_id && <span className="text-[11px] font-mono text-muted-foreground">#{e.resource_id.slice(0, 8)}</span>}
              <span className="flex-1 min-w-0 truncate">
                {e.actor?.name || e.actor?.email || <span className="text-muted-foreground">system</span>}
                {e.reason && <span className="text-muted-foreground"> · {e.reason}</span>}
              </span>
              <span className={`text-[11px] font-mono ${RESULT_COLOR[e.result] || ""}`}>{e.result}</span>
              <span className="text-[11px] font-mono text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
